import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useEventCallback } from 'usehooks-ts';

import { analytics } from '@/features/analytics/service';
import { getReceivedLikesKey } from '@/features/likes/hooks/useGetReceivedLikes/getReceivedLikes.options';
import { getMyProfileKey } from '@/features/profile/hooks/useGetMyProfile/getMyProfile.options';
import { getBalanceQueryKey } from '@/features/subscription/hooks/useGetBalance/getBalance.options';
import { useSubscribe } from '@/features/subscription/hooks/useSubscribe';
import { getCustomerInfoQueryKey } from '@/integrations/revenuecat/hooks/useCustomerInfo/useCustomerInfo.options';
import type { Package } from '@/integrations/revenuecat/types';

export interface UsePaywallCheckoutOptions {
  onCheckoutOpen: () => void;
  onCheckoutClose: (result: 'subscribed' | 'cancelled' | 'error') => void;
}

export function usePaywallCheckout({
  onCheckoutOpen,
  onCheckoutClose,
}: UsePaywallCheckoutOptions) {
  const queryClient = useQueryClient();
  const { mutate: subscribe, isPending } = useSubscribe();

  const handlePurchase = useEventCallback((selectedPackage: Package) => {
    analytics.captureEvent('Duos Subscription Checkout Clicked', {
      plan_id: selectedPackage.identifier,
      plan_type: selectedPackage.packageType,
      price: selectedPackage.product.priceString,
    });

    onCheckoutOpen();

    subscribe(selectedPackage, {
      onSuccess: ({ outcome }) => {
        if (outcome === 'subscribed') {
          void queryClient.invalidateQueries({
            queryKey: getCustomerInfoQueryKey(),
          });
          void queryClient.invalidateQueries({ queryKey: getMyProfileKey() });
          void queryClient.invalidateQueries({
            queryKey: getBalanceQueryKey(),
          });
          void queryClient.invalidateQueries({
            queryKey: getReceivedLikesKey(),
          });

          analytics.captureEvent('Duos Subscription Purchase Completed', {
            plan_id: selectedPackage.identifier,
            plan_type: selectedPackage.packageType,
            price: selectedPackage.product.priceString,
          });

          onCheckoutClose('subscribed');
        } else if (outcome === 'cancelled') {
          analytics.captureEvent('Duos Subscription Purchase Cancelled', {
            plan_id: selectedPackage.identifier,
            plan_type: selectedPackage.packageType,
            price: selectedPackage.product.priceString,
          });

          onCheckoutClose('cancelled');
        }
      },
      onError: error => {
        analytics.captureEvent('Duos Subscription Purchase Failed', {
          plan_id: selectedPackage.identifier,
          plan_type: selectedPackage.packageType,
          price: selectedPackage.product.priceString,
          error_message: error instanceof Error ? error.message : String(error),
        });

        toast.error('Subscription failed. Please try again.', {
          description: 'If this keeps happening, contact support.',
        });

        onCheckoutClose('error');
      },
    });
  });

  return {
    handlePurchase,
    isPending,
  };
}
